'use client';

import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
// eslint-disable-next-line import/no-unresolved
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import isClient from 'commons/isClient';
import useIsBrowser from 'hooks/useIsBrowser';
import { FACET_FILTER_VALUES } from '../search-state/utils';

const useFilters = () => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isBrowser = useIsBrowser();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [materialFilter, setMaterialFilter] = useState(''); // For separate filter buttons (Fabric/Leather/Wood)
  const [searchMaterialFilter, setSearchMaterialFilter] = useState(''); // For search dropdown materials
  const searchMaterialFilterRef = useRef(searchMaterialFilter);
  // Room lives in the URL, not state, so a query-only nav updates it without a
  // remount and without a state<->URL write loop (CON-7771).
  const roomFilter = searchParams?.get('room') || '';
  const [colorFilters, setColorFilters] = useState({});
  const [photoId, setPhotoId] = useState('');
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const isInitialized = useRef(false);

  // Keep ref in sync with state
  useEffect(() => {
    searchMaterialFilterRef.current = searchMaterialFilter;
  }, [searchMaterialFilter]);

  // Using window.location instead of useLocation/useHistory to prevent infinite loops

  useEffect(() => {
    if (!isInitialized.current) {
      const params = isClient()
        ? new URLSearchParams(window.location.search)
        : new URLSearchParams();

      const material = params.get('material');
      const searchMaterial = params.get('materialOption');
      const color = params.get('color');
      const productId = params.get('pid');
      const productName = params.get('pname');
      const photoIdParam = params.get('photoid');

      if (material) setMaterialFilter(material);
      if (searchMaterial) {
        setSearchMaterialFilter(searchMaterial);
        searchMaterialFilterRef.current = searchMaterial;
        setSearchQuery(searchMaterial);
      }

      if (color) {
        const colorFiltersObj = {};
        const colors = color.includes(',') ? color.split(',') : [color];
        colors.forEach(c => {
          colorFiltersObj[c] = true;
        });
        setColorFilters(colorFiltersObj);
      }

      if (productId && productName) {
        setSelectedProduct({ id: productId, name: productName });
        setSearchQuery(productName);
      }

      if (photoIdParam) setPhotoId(photoIdParam);

      isInitialized.current = true;
    }
  }, []);

  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Seed from the live URL so the URL-owned room param survives when a
    // state-owned filter changes.
    const newSearch = isBrowser
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();

    const searchParamsObj = {
      pid: selectedProduct?.id || '',
      pname: selectedProduct?.name || '',
      material: materialFilter || '',
      materialOption: searchMaterialFilter || '',
      color: Object.keys(colorFilters).join(',') || '',
      photoid: photoId || '',
    };

    Object.entries(searchParamsObj).forEach(([key, value]) => {
      if (value) {
        newSearch.set(key, value);
      } else {
        newSearch.delete(key);
      }
    });

    const newSearchString = newSearch.toString();
    const currentSearch = isBrowser ? window.location.search.substring(1) : '';

    if (newSearchString !== currentSearch) {
      const newUrl = `${
        isBrowser ? window.location.pathname : '/customer-photos'
      }${newSearchString ? `?${newSearchString}` : ''}`;
      if (isBrowser) {
        window.history.pushState(null, '', newUrl);
      }
    }
  }, [
    isBrowser,
    selectedProduct,
    materialFilter,
    searchMaterialFilter,
    colorFilters,
    photoId,
  ]);

  // Room writes go through the router so the URL stays the single source of
  // truth and there's no state<->URL write loop.
  const setRoomFilter = useCallback(
    value => {
      if (!isBrowser) return;
      const params = new URLSearchParams(window.location.search);
      if (value) {
        params.set('room', value);
      } else {
        params.delete('room');
      }
      const search = params.toString();
      router.replace(`${pathname}${search ? `?${search}` : ''}`, {
        scroll: false,
      });
    },
    [isBrowser, router, pathname]
  );

  const toggleColorFilter = color => {
    setColorFilters(prev => {
      const updated = { ...prev };
      if (updated[color]) {
        delete updated[color];
      } else {
        updated[color] = true;
      }
      return updated;
    });
  };

  const onImageSelect = useCallback(
    photoIdValue => {
      const updatedPhotoId = photoId !== photoIdValue ? photoIdValue : '';
      setPhotoId(updatedPhotoId);
    },
    [photoId]
  );

  const clearAllFilters = useCallback(() => {
    setSearchQuery('');
    setSelectedProduct(null);
    setMaterialFilter('');
    setSearchMaterialFilter('');
    setColorFilters({});
    setPhotoId('');
    if (isBrowser) {
      router.replace(pathname, { scroll: false });
    }
  }, [isBrowser, router, pathname]);

  const buildFacetFilters = useCallback(
    (overrides = {}) => {
      const currentRoomFilter =
        overrides.roomFilter !== undefined ? overrides.roomFilter : roomFilter;
      const currentMaterialFilter =
        overrides.materialFilter !== undefined
          ? overrides.materialFilter
          : materialFilter;
      const currentSelectedProduct =
        overrides.selectedProduct !== undefined
          ? overrides.selectedProduct
          : selectedProduct;
      const currentColorFilters =
        overrides.colorFilters !== undefined
          ? overrides.colorFilters
          : colorFilters;
      const facetFilters = [];

      if (currentRoomFilter) {
        facetFilters.push(`"${FACET_FILTER_VALUES.room}${currentRoomFilter}"`);
      }

      if (currentMaterialFilter) {
        facetFilters.push(
          `"${FACET_FILTER_VALUES.material}${currentMaterialFilter}"`
        );
      }

      if (currentSelectedProduct) {
        facetFilters.push(
          `"${FACET_FILTER_VALUES.product_id}${currentSelectedProduct.id}"`
        );
      }

      const colorsKeys = Object.keys(currentColorFilters);
      if (colorsKeys.length) {
        facetFilters.push(
          `[${colorsKeys
            .map(color => `"${FACET_FILTER_VALUES.color}${color}"`)
            .join(',')}]`
        );
      }

      return facetFilters.length ? `[${facetFilters.join(',')}]` : '';
    },
    [roomFilter, materialFilter, selectedProduct, colorFilters]
  );

  const isFiltered = useMemo(
    () =>
      !!(
        roomFilter ||
        materialFilter ||
        searchMaterialFilter ||
        selectedProduct ||
        Object.keys(colorFilters).length
      ),
    [
      roomFilter,
      materialFilter,
      searchMaterialFilter,
      selectedProduct,
      colorFilters,
    ]
  );

  const selectedFiltersList = useMemo(() => {
    const filters = [];

    if (selectedProduct) {
      filters.push({
        filterName: selectedProduct.name,
        onDeselect: () => {
          setSelectedProduct(null);
          setSearchQuery('');
        },
        name: 'product',
      });
    }

    if (searchMaterialFilter) {
      filters.push({
        filterName: searchMaterialFilter,
        onDeselect: () => {
          setSearchMaterialFilter('');
        },
        name: 'searchMaterial',
      });
    }

    return filters;
  }, [selectedProduct, searchMaterialFilter]);

  const numberOfActiveFilters = useMemo(() => {
    const materialFilterCount = materialFilter ? 1 : 0;
    const searchMaterialFilterCount = searchMaterialFilter ? 1 : 0;

    return materialFilterCount + searchMaterialFilterCount;
  }, [materialFilter, searchMaterialFilter]);

  const openMobileFilters = useCallback(() => setIsMobileFiltersOpen(true), []);
  const closeMobileFilters = useCallback(
    () => setIsMobileFiltersOpen(false),
    []
  );

  const actions = useMemo(
    () => ({
      setSearchQuery,
      setSelectedProduct,
      setMaterialFilter,
      setSearchMaterialFilter,
      setRoomFilter,
      toggleColorFilter,
      clearAllFilters,
      getNumberOfActiveFilters: () => numberOfActiveFilters,
      buildFacetFilters,
      onImageSelect,
      openMobileFilters,
      closeMobileFilters,
    }),
    [
      buildFacetFilters,
      onImageSelect,
      numberOfActiveFilters,
      clearAllFilters,
      setRoomFilter,
      openMobileFilters,
      closeMobileFilters,
    ]
  );

  const state = useMemo(
    () => ({
      searchQuery,
      selectedProduct,
      materialFilter,
      searchMaterialFilter,
      roomFilter,
      colorFilters,
      photoId,
      isMobileFiltersOpen,
      selectedFiltersList,
      isFiltered,
      isInitialized,
      numberOfActiveFilters,
    }),
    [
      searchQuery,
      selectedProduct,
      materialFilter,
      searchMaterialFilter,
      roomFilter,
      colorFilters,
      photoId,
      isMobileFiltersOpen,
      selectedFiltersList,
      isFiltered,
      isInitialized,
      numberOfActiveFilters,
    ]
  );

  return { state, actions };
};

export default useFilters;
